import {
  BMInputField,
  BMPrimitives,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemCrossClose from "@traveloka/icon-kit-web/react/IcSystemCrossClose";
import { Pressable, StyleSheet } from "react-native";
import { FullScreenDialog } from "../../../../components";
import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { useHotelAutocomplete } from "../../../../providers/api/HotelAutocompleteContext";
import { HotelSearchFormResource, HotelSearchFormValues } from "../types";
import EmptyResult from "./EmptyResult";
import EmptySearch from "./EmptySearch";
import SearchResult from "./SearchResult";
import { HotelSearchResponseItem } from "./types";

type Props = {
  isVisible: boolean;
  closeDialog: () => void;
  resource: HotelSearchFormResource;
  previousSearchData?: Partial<HotelSearchFormValues>[];
  selectPreviousSearchData: (
    searchData: Partial<HotelSearchFormValues>
  ) => void;
};

export default function HotelCitySearch(props: Props) {
  const {
    isVisible,
    closeDialog,
    resource,
    previousSearchData,
    selectPreviousSearchData,
  } = props;
  const bmTheme = useBMTheme();
  const {
    searchValue,
    handleSearchValueChange,
    results,
    isAutoCompleteLoading,
    isAutocompleteError,
    isAutocompleteNoResults,
  } = useHotelAutocomplete();
  const { CorporateHotelSearchForm } = useMobileContentResource();

  return (
    <FullScreenDialog
      style={{
        backgroundColor: bmTheme.BMComponents.BMStyleSurfaceBgcolorInventory,
      }}
      visible={isVisible}
    >
      <BMView
        row
        spacing="md"
        align="center"
        style={[
          styles.container,
          {
            borderBottomColor: bmTheme.BMComponents.BMStyleTextInkPrimaryInvert,
          },
        ]}
      >
        <BMView style={styles.input}>
          <BMInputField
            placeholder={CorporateHotelSearchForm.hotelSearchPlaceholder}
            label=""
            value={searchValue}
            onChange={(e) => handleSearchValueChange(e.nativeEvent.text)}
            buttonInsetProps={
              searchValue
                ? {
                    label: CorporateHotelSearchForm.clear,
                    onPress: () => handleSearchValueChange(""),
                  }
                : undefined
            }
          />
        </BMView>
        <Pressable style={styles.closeButton} onPress={closeDialog}>
          <IcSystemCrossClose width={24} height={24} />
        </Pressable>
      </BMView>
      <ShownComponent
        searchValue={searchValue}
        searchResult={results}
        resource={resource}
        isLoading={isAutoCompleteLoading}
        isError={isAutocompleteError}
        onCloseDialog={closeDialog}
        previousSearchData={previousSearchData}
        selectPreviousSearchData={selectPreviousSearchData}
        isAutocompleteNoResults={isAutocompleteNoResults}
      />
    </FullScreenDialog>
  );
}

type ShownComponentProps = {
  searchValue: string;
  searchResult: HotelSearchResponseItem[];
  resource: HotelSearchFormResource;
  isLoading: boolean;
  isError: boolean;
  onCloseDialog: () => void;
  previousSearchData?: Partial<HotelSearchFormValues>[];
  selectPreviousSearchData: (
    searchData: Partial<HotelSearchFormValues>
  ) => void;
  isAutocompleteNoResults: boolean;
};

function ShownComponent(props: ShownComponentProps) {
  const {
    searchValue,
    resource,
    searchResult,
    isError,
    onCloseDialog,
    previousSearchData,
    selectPreviousSearchData,
    isAutocompleteNoResults,
  } = props;
  if (!searchValue)
    return (
      <EmptySearch
        previousSearchData={previousSearchData}
        onCloseDialog={onCloseDialog}
        selectPreviousSearchData={selectPreviousSearchData}
      />
    );
  if (isAutocompleteNoResults)
    return <EmptyResult resource={resource} query={searchValue} />;
  return (
    <SearchResult
      query={searchValue}
      searchResults={searchResult}
      resource={resource}
      isError={isError}
      onCloseDialog={onCloseDialog}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: BMPrimitives.BMSize.md,
    paddingVertical: BMPrimitives.BMSize.xs,
    borderBottomWidth: BMPrimitives.BMStroke.sm,
  },
  input: {
    width: "90%",
  },
  closeButton: {
    marginLeft: "auto",
  },
});
